// 回放的純函式：設備 → 來源順序（ADR-003 §2.5）、日子與吸附。時間軸互動（縮放 / 拖曳 / 刻度）在 timeline.ts。
import type { NodeCoverageRow, RecorderAssignmentSpan, RecordingSource } from '@kinio/web-sdk';
import { playbackChannelOk } from '@kinio/web-sdk';
import type { VmsDevice } from '../../lib/devices';
import { hasData } from '../../lib/caps';

export const DAY_SEC = 86400;

/**
 * 本地錄影主機（硬碟優先，走 NVR 路徑；SPEC #vms-web-s5-19-1 STN-01）——只看能力，不看 TYPE / 型號：
 *   ① façade 回來了（capabilities 有值）：kinio_storage.disks 有錄影碟而且 kind 不是 sd（façade storage_kinds；沒帶 kind 的舊形狀算本機磁碟）→ 是，
 *      否則否（SD 卡也報在 kinio_storage，只有 SD 的基地台 / 攝影機不算；Station 開機、shadow 還沒回報前短暫是雲端優先，Eddy 2026-10-02）
 *   ② façade 還沒回來 / 失敗 → 過渡推測（TYPE / 型號 / AID），只在畫面上暫時用
 * device_class 不拿來判錄影順序（只判通道主機）：alphaseries-station-a1（基地台，SD）原本是 nvr，2026-09-28 改 gateway（platform 065）。
 * 工位模式的 Station（shadow kinio_station.info.seat_mode = true）一律不是（STN-01 工位模式，最優先）。
 */
export function isLocalRecorder(d: VmsDevice | undefined): boolean {
  if (!d || d.seatMode) return false;
  if (d.capabilities !== undefined) return (d.storageKinds ?? []).some((k) => k !== 'sd');
  if (d.typeCode === 'KST') return true;
  if (/station|gateway|\bgw\b|hub/i.test(d.model)) return false;
  if (/nvr/i.test(d.model)) return true;
  return d.aid.startsWith('CNV-');
}

/** 錄影主機（NVR、Kinio Station，含 Mac / Windows 版；工位模式不算）：總覽「錄影主機」卡與設備清單 ?type=recorder 同一個判斷（SPEC #vms-web-827）。 */
export function isRecorderHost(d: VmsDevice): boolean {
  return !d.seatMode && !d.parentAid && (d.children.length > 0 || isLocalRecorder(d));
}

/** @deprecated 用 isLocalRecorder（名字保留給舊呼叫端）。 */
export const isNvr = isLocalRecorder;

/**
 * 能當通道主機 / ONVIF 閘道的（§5.4、§5.19.1）：頂層設備，且有子設備、是本地錄影主機、
 * 或 device_class ∈ {nvr, gateway}；façade 還沒回來才看型號。工位模式的 Station 不列（STN-01 工位模式）。
 */
export function isChannelHost(d: VmsDevice): boolean {
  if (d.parentAid || d.seatMode) return false;
  if (d.children.length > 0 || isLocalRecorder(d)) return true;
  if (d.deviceClass) return d.deviceClass === 'nvr' || d.deviceClass === 'gateway';
  if (d.capabilities !== undefined) return false;
  return /station|gateway/i.test(d.model);
}

/** MyCam 手機攝影機（有 kinio_mycam）：本機錄影是手機本機，不是 SD（mycam-v0 §10.3）。 */
export function isPhoneCamera(d: VmsDevice | undefined): boolean {
  return hasData(d, 'kinio_mycam');
}

export interface SourcePlan {
  /** 依優先順序 */
  order: RecordingSource[];
  /** 本地索引 / 隧道要打的 AID（設備本體）與通道 */
  localAid: string;
  localCh: number;
  localKind: 'nvr' | 'sd' | 'phone';
  /** 開本地回放隧道時帶給平台記稽核的通道（`<主機>:ch<n>` 的 n；獨立攝影機沒有）——web-sdk PlaybackTunnel channel（R11-P1-1） */
  auditCh?: number;
  /** 雲端錄影掛在哪個 AID（通道子設備 有自己的 AID） */
  cloudAid: string;
}

/**
 * ADR-003 §2.5：本地錄影主機（NVR / Kinio Station / MyCam 電腦端自己的通道）硬碟優先 → 雲端 → 異地備份；
 * 其他攝影機：MyCam 電腦端（有替它錄才有，§5.19.3）→ 雲端 → 手機本機 / SD。
 */
export function sourcePlan(cam: VmsDevice, parent: VmsDevice | undefined): SourcePlan {
  const localKind = isLocalRecorder(parent) ? 'nvr' : isPhoneCamera(cam) ? 'phone' : 'sd';
  return {
    // NVR 的通道另有異地備份（§5.12 BAK-04）：本地、雲端都沒有的區間才用備份副本
    order: localKind === 'nvr' ? ['nvr', 'cloud', 'backup'] : ['node', 'cloud', localKind],
    localAid: cam.parentAid ?? cam.aid,
    localCh: cam.channel ?? 0,
    localKind,
    ...(cam.parentAid && playbackChannelOk(cam.channel) ? { auditCh: cam.channel } : {}),
    cloudAid: cam.aid,
  };
}

/**
 * 過渡：recorder_assignments 的讀取 API（MyCam C4，mycam-v0 §8.3）上線前，從覆蓋列推「哪段由哪台電腦端負責」——
 * 每台從它第一筆覆蓋開始；最後回報的那台負責到現在，其他台到它最後回報為止。API 上線後改用真的指派歷史。
 */
export function assignmentsFromCoverage(rows: NodeCoverageRow[]): RecorderAssignmentSpan[] {
  const by = new Map<string, { from: number; known: number }>();
  for (const r of rows) {
    const x = by.get(r.node);
    by.set(r.node, { from: Math.min(x?.from ?? Infinity, r.from), known: Math.max(x?.known ?? -Infinity, r.knownUntil, r.to) });
  }
  const latest = [...by.entries()].sort((a, b) => b[1].known - a[1].known)[0]?.[0];
  return [...by.entries()].map(([node, v]) => ({ node, from: v.from, to: node === latest ? null : v.known }));
}

export function todayKey(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 本地時區的當天 00:00（epoch 秒）。 */
export function dayStartSec(day: string): number {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0).getTime() / 1000;
}

/** 本地時區的當天長度（DST 那天是 23 / 25 小時）。 */
export function dayLengthSec(day: string): number {
  const [y, m, d] = day.split('-').map(Number);
  return (new Date(y, m - 1, d + 1).getTime() - new Date(y, m - 1, d).getTime()) / 1000;
}

/** 點時間軸：沒點中任何段、但 tolSec 內有段（24 小時視窗裡 15 秒的片段不到 1 像素）→ 吸到那段的開頭。 */
export function snapToSpan(spans: { start: number; end: number }[], at: number, tolSec: number): number {
  if (spans.some((s) => s.start <= at && at < s.end)) return at;
  let best: number | null = null;
  let bestD = Infinity;
  for (const s of spans) {
    // 點在一段之後（沒有錄影的地方）：只有短到畫不出來的段（比容差短）才往回吸；長段的尾巴之後不退回那一段的開頭，
    // 照 SPEC「點時間軸的落點」從之後最近一段播（第二輪 B2-11：點 23:00 不再從 22:26 播）
    if (at >= s.end && s.end - s.start >= tolSec) continue;
    const d = at < s.start ? s.start - at : at - s.end;
    if (d <= tolSec && d < bestD) [best, bestD] = [s.start, d];
  }
  return best ?? at;
}
